// features/coderunner/CodeRunnerContext.tsx
import { createContext, useContext } from "react";

type RefLike<T> = { current: T };

// Holds the state the (currently unshipped) code-running feature needs:
// what's active in the runner, the web preview doc, and the imperative
// hooks CodeTerminal/JSterm use to talk to whichever editor is running.
// Consumed by SnippetContainer (to know what's active) and by the
// components in features/coderunner. `activeTerminal` stays on
// WorkspaceContext instead of here — workspace layout (editor width, panel
// classes) needs it regardless of whether the code-runner feature is on.
export interface CodeRunnerCtx {
  activeCode?: string;
  setActiveCode: (c: string) => void;
  activeLanguage?: string;
  setActiveLanguage: (l: string) => void;
  activeWebPreview: boolean;
  setActiveWebPreview: (b: boolean) => void;
  previewDoc?: string;
  setPreviewDoc: (p: string) => void;
  outputRef: RefLike<string>;
  termWriteRef: RefLike<((s: string) => void) | null>;
  termReadLineRef: RefLike<(() => Promise<string>) | null>;
  jsRunRef: RefLike<((code: string) => void) | null>;
}

export const CodeRunnerContext = createContext<CodeRunnerCtx | undefined>(undefined);

export const useCodeRunner = () => {
  const ctx = useContext(CodeRunnerContext);
  if (!ctx) throw new Error("useCodeRunner must be used within CodeRunnerContext.Provider");
  return ctx;
};
